import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Select
} from '@material-ui/core'
import { AddCircleOutline as AddIcon } from '@material-ui/icons'
import React, { useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import { getTranslate } from '../../localization'
import { PenaltyBase } from '../../types/walletEnums'
import { PaymentTermDetail } from '../../types/walletPaymentTerm'
import useStyles from './styles/PaymentTermDetailsTable.styles'

interface PaymentTermDetailsTableProps {
  paymentTerms: PaymentTermDetail[]
  setPaymentTerms: (newDetails: PaymentTermDetail[]) => void
}

const PaymentTermDetailsTable: React.FC<PaymentTermDetailsTableProps> = ({
  paymentTerms,
  setPaymentTerms
}) => {
  const classes = useStyles()

  const [open, setOpen] = useState(false)
  const [newFromDay, setNewFromDay] = useState<number | string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleClose = () => {
    setOpen(false)
    setNewFromDay('')
  }

  const addPaymentTerm = () => {
    if (paymentTerms.length === 0) {
      const firstTerm: PaymentTermDetail = {
        fromDay: 0,
        toDay: undefined,
        infiniteTo: true,
        penaltyPercent: 0,
        penaltyFeeDaily: 0,
        penaltyBaseDaily: undefined,
        penaltyTermDetailID: undefined
      }
      setPaymentTerms([firstTerm])
      return
    }

    if (!newFromDay || isNaN(Number(newFromDay))) {
      setErrorMessage('Please enter a valid number')
      return
    }

    const enteredDay = Number(newFromDay)
    const maxFromDay = Math.max(...paymentTerms.map((term) => term.fromDay))

    if (enteredDay <= maxFromDay) {
      setErrorMessage(`The value must be greater than ${maxFromDay}`)
      return
    }

    const lastTerm = paymentTerms[paymentTerms.length - 1]
    lastTerm.toDay = enteredDay
    lastTerm.infiniteTo = false

    const newTerm: PaymentTermDetail = {
      fromDay: enteredDay + 1,
      toDay: undefined,
      infiniteTo: true,
      penaltyPercent: 0,
      penaltyFeeDaily: 0,
      penaltyBaseDaily: undefined,
      penaltyTermDetailID: undefined
    }

    setPaymentTerms([...paymentTerms, newTerm])
    handleClose()
  }

  const handleClickOpen = () => {
    if (paymentTerms.length > 0) {
      setErrorMessage('')
      setOpen(true)
    } else {
      addPaymentTerm()
    }
  }

  const handleInputChange = (
    index: number,
    field: keyof PaymentTermDetail,
    value: string | number
  ) => {
    const updatedTerms = [...paymentTerms]

    if (field === 'penaltyBaseDaily') {
      updatedTerms[index][field] = value as PenaltyBase
    } else if (
      field === 'penaltyPercent' ||
      field === 'penaltyFeeDaily' ||
      field === 'fromDay' ||
      field === 'toDay'
    ) {
      const numericValue =
        value === ''
          ? 0
          : typeof value === 'string'
          ? parseInt(value, 10)
          : value

      if (!isNaN(numericValue) && numericValue >= 0) {
        updatedTerms[index][field] = numericValue
      } else {
        updatedTerms[index][field] = 0
      }
    }

    setPaymentTerms(updatedTerms)
  }

  return (
    <div>
      <div className={classes.titleContainer}>
        <SectionTitle title="Payment Term Details" marginBottom />
      </div>
      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow className={classes.tableHeadRow}>
            <TableCell
              className={[classes.tableHeadCell, classes.firstColumn].join(
                ' '
              )}>
              {getTranslate('Time')}
            </TableCell>
            <TableCell className={classes.tableHeadCell}>
              {getTranslate('Penalty Percent')}
            </TableCell>
            <TableCell className={classes.tableHeadCell}>
              {getTranslate('Penalty Fee Daily')}
            </TableCell>
            <TableCell className={classes.tableHeadCell}>
              {getTranslate('Penalty Base')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paymentTerms.map((term, index) => (
            <TableRow key={index} className={classes.tableHeadRow}>
              <TableCell
                className={[classes.tableCell, classes.firstColumn].join(' ')}>
                {term.fromDay} day to{' '}
                {term.toDay ? `${term.toDay} day` : 'infinity day'}
              </TableCell>
              <TableCell className={classes.tableCell}>
                <TextField
                  value={term.penaltyPercent}
                  onChange={(e) =>
                    handleInputChange(index, 'penaltyPercent', e.target.value)
                  }
                  fullWidth
                  variant="standard"
                  type="text"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    className: classes.centeredTextField // Add a class for custom styling
                  }}
                  InputProps={{
                    disableUnderline: true,
                    className: classes.noBorderTextField
                  }}
                />
              </TableCell>
              <TableCell className={classes.tableCell}>
                <TextField
                  value={term.penaltyFeeDaily}
                  onChange={(e) =>
                    handleInputChange(index, 'penaltyFeeDaily', e.target.value)
                  }
                  fullWidth
                  variant="standard"
                  type="text"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    className: classes.centeredTextField // Add a class for custom styling
                  }}
                  InputProps={{
                    disableUnderline: true,
                    className: classes.noBorderTextField
                  }}
                />
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Select
                  value={term.penaltyBaseDaily || ''}
                  onChange={(event) =>
                    handleInputChange(
                      index,
                      'penaltyBaseDaily',
                      event.target.value as string
                    )
                  }
                  fullWidth
                  variant="standard" // Ensure standard variant for no borders
                  displayEmpty
                  IconComponent={() => null} // Hide the dropdown icon
                  inputProps={{
                    className: classes.noBorderSelect // Apply custom styling
                  }}
                  SelectDisplayProps={{
                    style: {
                      padding: 0 // Remove padding inside the select
                    }
                  }}
                  disableUnderline // Add this to ensure no underline is added
                >
                  <MenuItem value="originalTransactionAmount">
                    {getTranslate('Original Transaction Amount')}
                  </MenuItem>
                  <MenuItem value="compound">
                    {getTranslate('Compound')}
                  </MenuItem>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div>
        <IconButton onClick={handleClickOpen} color="primary">
          <AddIcon />
        </IconButton>
        {getTranslate('Add Days')}
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add Time</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="From Day / To Day"
            type="number"
            fullWidth
            value={newFromDay}
            onChange={(e) => setNewFromDay(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={addPaymentTerm} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default PaymentTermDetailsTable
